import React, { useEffect, useId, useState, useRef } from 'react';
import { Box, Typography, Button, Skeleton, Paper, List, ListItem, Portal } from '@mui/material';
import SearchInput from '../../../common/SearchInputField';
import { useSelector } from 'react-redux';
import { loadGoogleFont } from "../../../../utils";

const formatColor = (color) => {
  if (!color) return null;
  if (/^#[0-9a-fA-F]{8}$/.test(color)) return color.slice(0, 7);
  return color;
};

const useScopedFontStyle = (scopeId, fontFamily) => {
  useEffect(() => {
    if (!scopeId || !fontFamily) return;
    const styleId = `kb-font-scope-${scopeId}`;

    let tag = document.getElementById(styleId);
    if (tag) tag.remove();

    tag = document.createElement('style');
    tag.id = styleId;
    tag.innerHTML = `
      [data-kb-font-id="${scopeId}"] .MuiTypography-root,
      [data-kb-font-id="${scopeId}"] .MuiButton-root,
      [data-kb-font-id="${scopeId}"] .MuiInputBase-root,
      [data-kb-font-id="${scopeId}"] .MuiInputLabel-root,
      [data-kb-font-id="${scopeId}"] .MuiFormLabel-root,
      [data-kb-font-id="${scopeId}"] .MuiOutlinedInput-root,
      [data-kb-font-id="${scopeId}"] .MuiOutlinedInput-input,
      [data-kb-font-id="${scopeId}"] .MuiInputBase-input,
      [data-kb-font-id="${scopeId}"] * {
        font-family: "${fontFamily}", Inter, sans-serif !important;
      }
    `;
    document.head.appendChild(tag);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [scopeId, fontFamily]);
};

const HelpSection = ({
  title = 'How can we help?',
  description,
  searchQuery,
  onSearchChange,
  productName = 'TrustChat',
  onSearchShow = true,
  onSearchSubmit,
  onKeyDown,
  onSuggestionSelect,
  appendProductName = true,
  suggestions = [],
  onSuggestionClick,
}) => {
  const { data: kbSettings, initialized } = useSelector((state) => state.externalKbSettings);
  const { loading: featuredLoading, } = useSelector((state) => state.featuredArticlesExternal);
  const brandColor = formatColor(kbSettings?.data?.brand_color);
  const accentColor = formatColor(kbSettings?.data?.accent_color);
  const fontFamily = kbSettings?.data?.font_family || 'Inter';

  const rawId = useId();
  const scopeId = rawId.replace(/:/g, '');

  // ── Dropdown state ────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const inputBoxRef = useRef(null);

  // Recompute dropdown position on open, scroll, and resize
  useEffect(() => {
    const updatePosition = () => {
      if (!open || !inputBoxRef.current) return;
      const rect = inputBoxRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, searchQuery]);

  const filteredSuggestions = searchQuery?.trim()
    ? suggestions.filter((s) => {
      const label = s.label ?? s.title ?? '';
      return label.toLowerCase().includes(searchQuery.toLowerCase());
    })
    : suggestions;

  // Open when there are filtered results and a query exists
  useEffect(() => {
    setOpen(filteredSuggestions.length > 0 && !!searchQuery?.trim());
  }, [filteredSuggestions.length, searchQuery]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);


  const handleSelect = (suggestion) => {
    const label = suggestion.label ?? suggestion.title ?? '';
    // Update input text so it shows the selected title
    onSearchChange({ target: { value: label } });
    // Close dropdown immediately
    setOpen(false);
    // Navigate to article detail — use whichever prop is provided
    const navigateFn = onSuggestionClick ?? onSuggestionSelect;
    navigateFn?.(suggestion);
    // ✅ intentionally NOT calling onSearchSubmit — that would go to search results page
  };
  // ── END dropdown logic ────────────────────────────────────────────

  useEffect(() => { loadGoogleFont(fontFamily); }, [fontFamily]);
  useScopedFontStyle(scopeId, fontFamily);

  const bgColor = brandColor || '#F7F8F9';
  const textColor = brandColor ? '#fff' : '#0C141B';
  const subTextColor = brandColor ? '#fff' : '#405261';
  const btnBg = brandColor ? '#fff' : '#148DFD';
  const btnTextColor = brandColor ? (accentColor || '#148DFD') : '#fff';

  const containerSx = {
    width: {
      xs: 'calc(100% + 48px)',
      sm: 'calc(100% + 120px)',
      md: 'calc(100% + 200px)',
      lg: 'calc(100% + 320px)',
      xl: 'calc(100% + 448px)',
    },
    mx: { xs: '-24px', sm: '-60px', md: '-100px', lg: '-160px', xl: '-224px' },
    maxHeight: '500px',
    backgroundColor: bgColor,
    display: 'flex',
    marginTop: { xs: '-2px', sm: '-2px', md: '-2px', lg: '-3px', xl: '-3px' },
    paddingBottom: { xs: '27px', sm: '57px', lg: '95px', xl: '129px' },
    paddingTop: { xs: '40px', sm: '52px', lg: '85px', xl: '140px' },
    flexDirection: 'column',
    alignItems: 'center',
    border: 'none',
    borderTop: 'none',
    justifyContent: 'center',
    px: { xs: '24px', md: 0 },
  };

  if (!initialized || featuredLoading) {
    return (
      <Box sx={containerSx}>
        <Skeleton variant="rectangular" width={400} height={60} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rectangular" width={300} height={30} sx={{ borderRadius: '8px', mt: 2 }} />
        {onSearchShow && (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', maxWidth: 500, justifyContent: 'center', mt: 2 }}>
            <Skeleton variant="rectangular" width={320} height={50} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" width={120} height={50} sx={{ borderRadius: '8px' }} />
          </Box>
        )}
      </Box>
    );
  }

  const isSearchResultTitle = title.startsWith("Here's what we found for");
  const isNoArticlesDesc = description.startsWith('No articles were found');
  const isQueryEmpty = !searchQuery?.trim();
  return (
    <Box
      data-kb-font-id={scopeId}
      sx={{
        ...containerSx,
        overflow: 'visible',
        boxSizing: 'border-box',
      }}
    >
      {/* Title */}
      {isSearchResultTitle ? (
        <Typography
          sx={{
            color: textColor,
            textAlign: 'center',
            fontSize: { xs: '20px', sm: '30px', md: '24px', lg: '36px', xl: '44px' },
            fontWeight: 400,
            lineHeight: { xs: '19px', sm: '38px', md: '24px', lg: '46px', xl: '58px' },
            marginBottom: { xs: '5px', sm: '7px', md: '24px', lg: '16px', xl: '24px' },
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          Here's what we found for{' '}
          <Typography
            component="span"
            sx={{
              color: textColor,
              fontSize: { xs: '20px', sm: '30px', md: '24px', lg: '36px', xl: '44px' },
              fontWeight: 700,
            }}
          >
            {searchQuery}
          </Typography>
        </Typography>
      ) : (
        <Typography
          sx={{
            color: textColor,
            textAlign: 'center',
            fontSize: { xs: '20px', sm: '34px', md: '60px', lg: '60px', xl: '84px' },
            fontWeight: 700,
            lineHeight: { xs: '19px', sm: '38px', md: '58px', lg: '58px', xl: '94px' },
            marginBottom: { xs: '5px', sm: '7px', md: '24px', lg: '24px', xl: '24px' },
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {title}
        </Typography>
      )}

      {/* Description */}
      {isNoArticlesDesc ? (
        <Typography
          sx={{
            color: subTextColor,
            fontSize: { xs: '9px', sm: '18px', md: '24px', lg: '18px', xl: '24px' },
            fontWeight: 400,
            lineHeight: { xs: '16px', sm: '15px', md: '38px', lg: '28px', xl: '38px' },
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          No articles were found. Feel free to try again or{' '}
          <Typography
            component="a"
            sx={{
              color: subTextColor,
              fontSize: { xs: '9px', sm: '18px', md: '24px', lg: '18px', xl: '24px' },
              fontWeight: 400,
              textDecorationLine: 'underline',
              cursor: 'pointer',
            }}
          >
            contact us
          </Typography>
        </Typography>
      ) : (
        <Typography
          sx={{
            color: subTextColor,
            textAlign: 'center',
            fontSize: { xs: '9px', sm: '18px', md: '24px', lg: '18px', xl: '24px' },
            fontWeight: 400,
            lineHeight: { xs: '16px', sm: '15px', md: '38px', lg: '28px', xl: '38px' },
          }}
        >
          {description}{productName}
        </Typography>
      )}

      {/* Search Bar */}
      {onSearchShow && (
        <Box
          ref={wrapperRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            gap: 1,
            marginTop: { xs: '19px', sm: '16px', lg: '41px' },
            position: 'relative',
          }}
        >
          <Box
            ref={inputBoxRef}
            sx={{
              '& .Input-headingcontainer01-search': {
                marginTop: '-5px !important',
                paddingTop: '0 !important',
                height: 'auto !important',
                minHeight: 'unset !important',
              },
            }}
          >
            <SearchInput
              title=""
              placeholder="Search"
              className="SearchInputField-kb-layout"
              value={searchQuery}
              onChange={onSearchChange}
              onKeyDown={onKeyDown}
            />
          </Box>

          <Button
            variant="contained"
            onClick={onSearchSubmit}
            className="kb-search-btn-blink"
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{
              borderRadius: '8px',
              minWidth: { xs: '70px', sm: '100px', md: '180px' },
              textTransform: 'none',
              height: { xs: '40px', sm: '40px', md: '60px' },
              background: `${btnBg} !important`,
              color: `${btnTextColor} !important`,
              fontSize: { xs: '12px', sm: '14px', md: '16px' },
              fontWeight: 600,
              lineHeight: 'normal',
              cursor: isQueryEmpty ? 'not-allowed' : 'pointer',
              boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05) !important',
              outline: 'none !important',
              '&:hover': {
                background: brandColor ? '#f5f5f5' : '#0077CC',
              },
            }}
          >
            Search
          </Button>

          {/* Dropdown — Portal so it escapes any parent overflow:hidden */}
          {open && (
            <Portal>
              <Paper
                elevation={3}
                sx={{
                  ...dropdownStyle,
                  borderRadius: '8px',
                  maxHeight: '160px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  border: '1px solid #D5DCE1',
                  background: '#FFF',
                  py: '10px',
                  px: '20px',
                  boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
                }}
              >
                <List disablePadding>
                  {filteredSuggestions.map((option, index) => {
                    const label = option.label ?? option.title ?? '';
                    const query = searchQuery ?? '';

                    const matchIdx = label.toLowerCase().indexOf(query.toLowerCase());

                    return (
                      <ListItem
                        key={option.id ?? index}
                        onMouseDown={(e) => {
                          // preventDefault stops the input from losing focus
                          // before our click handler fires
                          e.preventDefault();
                          handleSelect(option);
                        }}
                        sx={{
                          cursor: 'pointer',
                          p: 0,
                          marginTop: '5px',
                          backgroundColor: '#fff',
                          '&:hover': { backgroundColor: '#fff' },
                        }}
                      >
                        <Typography sx={{
                          color: '#061522',
                          fontFamily: 'Inter',
                          fontSize: { xs: '12px', sm: '14px', md: '16px' },
                          height: '40px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '40px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}>
                          {matchIdx === -1 ? label : (
                            <>
                              {label.slice(0, matchIdx)}
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: { xs: '12px', sm: '14px', md: '16px' },
                                  borderRadius: '5px 0 0 5px',
                                  background: 'rgba(20, 141, 253, 0.10)',
                                }}
                              >
                                {label.slice(matchIdx, matchIdx + query.length)}
                              </Typography>
                              {label.slice(matchIdx + query.length)}
                            </>
                          )}
                          {option?.subtitle && (
                            <Typography component="span" sx={{ fontSize: '14px', color: '#9BA8B3' }}>
                              {' '}- {option?.subtitle}
                            </Typography>
                          )}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Portal>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HelpSection;